import React, { useContext, useEffect, useState } from 'react';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useScrollableTableHeight } from '../hooks/useScrollableTableHeight';
import Toggle from './Toggle';
import ReviewDropdown from './ReviewDropdown';
import TooltipOverlay from 'Components/Common/components/Button/TooltipOverlay';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import { Sort, ArrowUp, ArrowDown } from 'Components/DesignSystem/Iconography/Icons';
import SingleSelectWithCreate from 'Pages/GMS/CatalogManagement/modals/components/SingleSelectWithCreate';
import MultiSelectWithCreate from 'Pages/GMS/CatalogManagement/modals/components/MultiSelectWithCreate';

// Phase 2: the persisted shape for `mapped_attributes` / `mapped_attribute_value`
// is `string[]`. This helper coerces any cell value (legacy string, string[],
// null/undefined) to a clean string[] for the dropdown to render.
const toStringArray = (raw: unknown): string[] => {
    if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (typeof raw === 'string' && raw.length > 0) return [raw];
    return [];
};

type BaseMappingTableProps<T> = {
    // Field mappings
    fields: {
        incoming: keyof T;
        mapped: keyof T;
        considered: keyof T;
        reviewed: keyof T;
    };

    // Column labels
    labels: {
        incoming: string;
        mapped: string;
    };

    // Optional validation function for toggle
    validateToggle?: (item: T, allCategories: MappingCategory[]) => { isDisabled: boolean; reason?: string };

    // Actions
    onUpdate: (id: string, changes: Partial<T>) => void;

    // Row actions
    rowActions: RowActionsData | null;
    setRowActions: (actions: RowActionsData | null) => void;

    /**
     * When provided, the "mapped" cell becomes a search-as-you-type dropdown
     * (SingleSelectWithCreate) instead of a free-text input. The caller loads
     * options from the server — typically `getCategoryDropdownOptions` which
     * already serves both attributes (no attributeName) and attribute values
     * (with attributeName).
     */
    loadMappedOptions?: (search: string, page: number) => Promise<{ options: { value: string; label: string }[]; hasMore: boolean }>;

    /**
     * Callback fired when the user clicks "Create" in the dropdown. The caller
     * typically opens CreateAttributeModal / CreateAttributeValueModal for the
     * given row and, on success, calls onUpdate with the new mapped value.
     */
    onCreateMapped?: (item: T) => void;

    /**
     * Phase 2: when true (the default), the mapped column renders a multi-
     * select and the underlying field is a string[]. Callers that explicitly
     * need the legacy single-select can pass false.
     */
    isMulti?: boolean;

    // Optional customization
    className?: string;
    testIdPrefix?: string;
};

type SortOption = 'incoming' | 'mapped' | 'considered' | 'reviewed';

function BaseMappingTable<T extends Record<string, any>>({
    fields,
    labels,
    onUpdate,
    setRowActions,
    validateToggle,
    loadMappedOptions,
    onCreateMapped,
    isMulti = true,
    className = '',
    testIdPrefix = 'mapping'
}: BaseMappingTableProps<T>) {
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const {
        mappingState,
        isEditMode,
        readPermission,
        getNoDataMessage,
        getAllCategories,
        activeTab,
        pagination,
        loadMoreData,
        sortState,
        handleSortChange,
        isLoadingAttributeValues,
        getCurrentTabData
    } = useContext<MappingContextType>(MappingContext);

    // Get data directly from context
    const filteredData = getCurrentTabData() as unknown as T[];

    // Get pagination state for current tab
    const getCurrentPaginationState = () => {
        switch (activeTab) {
            case 'attribute':
                return pagination.attributes;
            case 'attributevalue':
                return pagination.attributeValues;
            case 'category':
                return pagination.categories;
            default:
                return { hasMore: false, loading: false, totalItems: 0, currentPage: 0, uniqueMappedCount: 0, pendingReviewCount: 0 };
        }
    };

    const currentPagination = getCurrentPaginationState();
    const { hasMore, loading: paginationLoading } = currentPagination;

    // Setup infinite scroll
    const sentinelRef = useInfiniteScroll(loadMoreData, hasMore, paginationLoading);

    // Size the table's scroll region to the remaining viewport so only the
    // table body scrolls; the toolbar + sticky column header stay put.
    const { ref: scrollContainerRef, maxHeight: tableMaxHeight } = useScrollableTableHeight();

    useEffect(() => {
        // Clear selection when exiting edit mode
        if (!isEditMode) {
            setSelectedRows(new Set());
        }
    }, [isEditMode]);

    // No client-side sorting - data comes pre-sorted from API
    const processedData = filteredData;

    // Map column names to API field names
    // Note: Frontend uses camelCase (isConsidered, isReviewed) but API expects snake_case (is_considered, is_reviewed)
    const getApiFieldName = (column: SortOption): string => {
        switch (column) {
            case 'incoming':
                return String(fields.incoming);
            case 'mapped':
                return String(fields.mapped);
            case 'considered':
                return 'is_considered'; // API expects snake_case
            case 'reviewed':
                return 'is_reviewed'; // API expects snake_case
            default:
                return String(fields.incoming);
        }
    };

    const handleSort = (column: SortOption) => {
        const apiFieldName = getApiFieldName(column);
        handleSortChange(apiFieldName);
    };

    // Phase 2: the mapped field is a string[] in the persisted shape. We
    // accept either string or string[] from the dropdown component and
    // normalize to whichever shape the caller's reducer/state expects.
    // `isMulti` defaults to true → state holds string[]. Set isMulti=false
    // to keep scalar storage (legacy callers).
    const handleMappedFieldChange = (item: T, newValue: string | string[]) => {
        const normalized = isMulti
            ? (Array.isArray(newValue) ? newValue : toStringArray(newValue))
            : (Array.isArray(newValue) ? (newValue[0] ?? '') : newValue);
        onUpdate(String(item[fields.incoming]), { [fields.mapped]: normalized } as Partial<T>);
    };

    // Checkbox selection handlers
    const handleSelectRow = (itemId: string, checked: boolean) => {
        const newSelectedRows = new Set(selectedRows);
        if (checked) {
            newSelectedRows.add(itemId);
        } else {
            newSelectedRows.delete(itemId);
        }
        setSelectedRows(newSelectedRows);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = new Set(processedData.map(item => String(item[fields.incoming])));
            setSelectedRows(allIds);
        } else {
            setSelectedRows(new Set());
        }
    };

    const isAllSelected = processedData.length > 0 && processedData.every(item => selectedRows.has(String(item[fields.incoming])));
    const isIndeterminate = processedData.some(item => selectedRows.has(String(item[fields.incoming]))) && !isAllSelected;

    // Bulk row actions
    const handleBulkReviewUpdate = (reviewStatus: boolean) => {
        selectedRows.forEach(itemId => {
            onUpdate(itemId, { [fields.reviewed]: reviewStatus } as Partial<T>);
        });
        setSelectedRows(new Set());
    };

    const markAsReviewed = () => handleBulkReviewUpdate(true);
    const markAsReviewPending = () => handleBulkReviewUpdate(false);

    // Provide row actions data to context
    useEffect(() => {
        const rowActionsData: RowActionsData = {
            selectedRows,
            selectedCount: selectedRows.size,
            markAsReviewed,
            markAsReviewPending,
            hasSelection: selectedRows.size > 0
        };

        setRowActions(isEditMode && selectedRows.size > 0 ? rowActionsData : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows.size, isEditMode, setRowActions]); // markAsReviewed and markAsReviewPending are stable callbacks

    const rowPadding = 'tw-py-2 tw-px-3';
    // The DS TableHeader cell grammar (title-header IS 14px/600; the header
    // labels the data below it, so it sits at ink-secondary behind the cells).
    const headerClasses = "tw-cursor-pointer tw-px-3 tw-py-2 tw-text-left tw-text-title-header tw-text-ink-secondary tw-whitespace-nowrap";
    const truncatedCell = "tw-truncate tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap";

    // Character cap applied to long Incoming / Mapped values on top of the
    // CSS truncation, so both the displayed text and the underlying width
    // stay predictable. Full value is always available via the `title` tooltip.
    const MAX_VALUE_CHARS = 37;
    const truncateChars = (s: string, max: number = MAX_VALUE_CHARS) =>
        s.length > max ? `${s.slice(0, max)}…` : s;

    const getColumnWidth = (column: string): string => {
        /*
         * The four CONTENT columns, as percentages of the table. The two narrow ones
         * are not here: the checkbox and `#` headers carry fixed pixel widths
         * (`tw-w-12` / `tw-w-10`), which the browser takes off the top before these
         * percentages are resolved.
         *
         * § 324 — they used to be `3%` and `3%`/`4%` too, and that was the bug. Under
         * `table-fixed` a 3% column is 3% of whatever the pane happens to be, while the
         * checkbox inside it needs a FIXED 40px: 16px for the DS `Checkbox` at `sm`
         * plus the cell's own 24px of `tw-px-3`. So the column only held its content
         * when the table was at least ~1333px wide, and below that the box overflowed
         * its content box and sat hard against the table's left border instead of on
         * the 12px inset (reported on Attribute Value Mapping, 2026-09-22, where the
         * table lives in the right-hand pane and is nowhere near that wide).
         *
         * A control's width is not a proportion of the page. Pixels for the columns
         * that hold one, percentages for the columns that hold text.
         *
         * Totals are therefore 32+32+12+18 = 94 in edit mode and 33+33+12+18 = 96
         * outside it, plus 88px of fixed columns. The remainder is the browser's
         * proportional slack, which is what keeps this stable as the pane resizes.
         */
        const hasCheckbox = isEditMode;

        switch (column) {
            case "incoming":
                return hasCheckbox ? `tw-w-[32%]` : `tw-w-[33%]`;
            case "mapped":
                return hasCheckbox ? `tw-w-[32%]` : `tw-w-[33%]`;
            case "considered":
                return `tw-w-[12%]`;
            case "reviewed":
                return `tw-w-[18%]`;
            default:
                return `tw-w-[5%]`;
        }
    };

    const renderHeaderCell = (column: SortOption, label: string) => (
        <th key={column} className={`${headerClasses} ${getColumnWidth(column)}`} onClick={() => handleSort(column)}>
            <div className="tw-flex tw-items-center tw-gap-1">
                <span>{label}</span>
                {sortElement(column)}
            </div>
        </th>
    );

    // The DS TableHeader sort slot: Sort (unsorted) / ArrowUp / ArrowDown in a
    // 20px ink-tertiary box, replacing the FontAwesome fa-sort glyphs.
    const sortElement = (column: string) => {
        // Get current sort state for active tab
        const currentSort = activeTab === 'attribute'
            ? sortState.attributes
            : activeTab === 'attributevalue'
                ? sortState.attributeValues
                : sortState.categories;

        const apiFieldName = getApiFieldName(column as SortOption);
        const isActive = currentSort.sortBy === apiFieldName;

        return (
            <span
                aria-hidden="true"
                className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-ink-tertiary"
            >
                {isActive ? (currentSort.sortOrder === 'asc' ? <ArrowUp /> : <ArrowDown />) : <Sort />}
            </span>
        );
    };

    // Show loading skeleton when main data is loading OR when attribute values are loading
    if (mappingState.loading || (activeTab === 'attributevalue' && isLoadingAttributeValues)) {
        return <div><TableSkeletonElement /></div>;
    }

    if (!readPermission) {
        return <NoDataComponent title='Permission Denied!' message="You don't have enough permission to access this feature." />;
    }

    if (!filteredData.length) {
        const noDataMsg = getNoDataMessage();
        return <NoDataComponent title={noDataMsg.title} message={noDataMsg.message} />;
    }

    return (
        <div
            className={`tw-border tw-border-solid tw-rounded-md tw-mt-3 tw-border-edge tw-mb-4 ${className}`}
        >
            <div
                ref={scrollContainerRef}
                className="table-container"
                style={{ width: '100%', maxHeight: tableMaxHeight, overflowX: 'auto', overflowY: 'auto' }}
            >
                <table className="tw-bg-tone-neutral-tint tw-w-full tw-table-fixed">
                    <thead className="tw-sticky tw-top-0 tw-z-20 tw-bg-tone-neutral-tint">
                        <tr>
                            {isEditMode && (
                                <th className="tw-w-12 tw-text-center tw-px-3 tw-py-2">
                                    <Checkbox
                                        size="sm"
                                        checked={isAllSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={(checked) => handleSelectAll(checked)}
                                        aria-label="Select all rows"
                                        className="tw-justify-center"
                                        data-testid={`${testIdPrefix}-select-all-checkbox`}
                                    />
                                </th>
                            )}
                            <th className="tw-w-10 tw-text-center tw-py-2 tw-text-title-header tw-text-ink-secondary">#</th>
                            {renderHeaderCell("incoming", labels.incoming)}
                            {renderHeaderCell("mapped", labels.mapped)}
                            {renderHeaderCell("considered", "Consider")}
                            {renderHeaderCell("reviewed", "Review")}
                        </tr>
                    </thead>
                    <tbody>
                        {processedData.map((item, index) => {
                            return (
                                <React.Fragment key={String(item[fields.incoming])}>
                                    <tr className="hover:tw-bg-surface-hover">
                                        {isEditMode && (
                                            <td className={`${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"} tw-px-3 tw-py-2 tw-text-center`}>
                                                <Checkbox
                                                    size="sm"
                                                    checked={selectedRows.has(String(item[fields.incoming]))}
                                                    onChange={(checked) => handleSelectRow(String(item[fields.incoming]), checked)}
                                                    aria-label="Select row"
                                                    className="tw-justify-center"
                                                    data-testid={`${testIdPrefix}-checkbox-${item[fields.incoming]}`}
                                                />
                                            </td>
                                        )}
                                        <td className={`${rowPadding} ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"} tw-text-body-primary tw-text-ink tw-text-center`}>
                                            {index + 1}
                                        </td>
                                        <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"} tw-text-body-primary tw-text-accent`} title={String(item[fields.incoming])}>
                                            {truncateChars(String(item[fields.incoming]))}
                                        </td>
                                        <td className={`${rowPadding} tw-max-w-0 tw-overflow-visible ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                                            {(() => {
                                                const allCategories = getAllCategories();
                                                const validationResult = validateToggle ? validateToggle(item, allCategories) : { isDisabled: false };
                                                const isDisabled = !isEditMode || validationResult.isDisabled;

                                                // Phase 2: default to MultiSelectWithCreate when loadMappedOptions
                                                // is supplied. Callers can explicitly opt out via isMulti=false to
                                                // preserve the legacy single-select behaviour.
                                                const mappedRaw = item[fields.mapped];
                                                const mappedArray = toStringArray(mappedRaw);
                                                const mappedScalar = mappedArray.length > 0 ? mappedArray[0] : '';

                                                const inputElement = loadMappedOptions ? (
                                                    isMulti ? (
                                                        <MultiSelectWithCreate
                                                            value={mappedArray}
                                                            onChange={(v) => handleMappedFieldChange(item, v)}
                                                            options={[]}
                                                            loadOptions={loadMappedOptions}
                                                            onCreateNew={() => onCreateMapped?.(item)}
                                                            placeholder={`Select ${labels.mapped.toLowerCase()}`}
                                                            disabled={isDisabled}
                                                        />
                                                    ) : (
                                                        <SingleSelectWithCreate
                                                            value={mappedScalar}
                                                            onChange={(v) => handleMappedFieldChange(item, v)}
                                                            options={[]}
                                                            loadOptions={loadMappedOptions}
                                                            onCreateNew={() => onCreateMapped?.(item)}
                                                            placeholder={`Select ${labels.mapped.toLowerCase()}`}
                                                            disabled={isDisabled}
                                                        />
                                                    )
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={mappedScalar}
                                                        onChange={(e) => handleMappedFieldChange(item, e.target.value)}
                                                        className="tw-w-full tw-py-1 tw-px-3 tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge-strong tw-flex tw-justify-start tw-items-center tw-gap-2.5 tw-text-ink tw-text-sm tw-border-0 placeholder:tw-text-ink-secondary placeholder:tw-text-sm"
                                                        data-testid={`${testIdPrefix}-mapped-input-${item[fields.incoming]}`}
                                                        disabled={isDisabled}
                                                    />
                                                );

                                                if (isEditMode && isDisabled) {
                                                    return (
                                                        <TooltipOverlay message="This value is used in category mapping" block>
                                                            {inputElement}
                                                        </TooltipOverlay>
                                                    );
                                                }

                                                if (!isDisabled) {
                                                    return inputElement;
                                                }

                                                // Phase 2 view mode: render mapped value(s) as chips matching
                                                // the Products list pattern (see AttributeMultiSelectChips.tsx).
                                                // Per-chip `title` only — no wrapper tooltip — so hovering a
                                                // gap doesn't pop a giant comma-joined list.
                                                const mappedChips = toStringArray(item[fields.mapped]);
                                                if (mappedChips.length === 0) {
                                                    return (
                                                        <span className="tw-text-ink-disabled tw-text-sm tw-italic">—</span>
                                                    );
                                                }
                                                return (
                                                    <div className="tw-flex tw-flex-wrap tw-gap-1">
                                                        {mappedChips.map((chip, i) => (
                                                            <span
                                                                key={`${chip}-${i}`}
                                                                className="tw-flex tw-items-center tw-gap-1 tw-bg-accent-tint tw-text-accent tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-max-w-[160px]"
                                                                title={chip}
                                                            >
                                                                <span className="tw-truncate tw-max-w-[140px]">
                                                                    {chip}
                                                                </span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className={`${rowPadding} ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"} tw-text-start`}>
                                            {(() => {
                                                const mappedValue = item[fields.mapped];
                                                const mappedAsArray = toStringArray(mappedValue);
                                                const isEmpty = mappedAsArray.length === 0;

                                                // Get validation result if validator is provided
                                                const allCategories = getAllCategories();
                                                const validationResult = validateToggle ? validateToggle(item, allCategories) : { isDisabled: false };

                                                const isDisabled = !isEditMode || isEmpty || validationResult.isDisabled;

                                                const toggleElement = (
                                                    <Toggle
                                                        name={`${testIdPrefix}-toggle-${item[fields.incoming]}`}
                                                        value={Boolean(item[fields.considered])}
                                                        onChange={(checked) => onUpdate(String(item[fields.incoming]), { [fields.considered]: checked } as Partial<T>)}
                                                        disabled={isDisabled}
                                                    />
                                                );

                                                // Show tooltip if there's a validation reason or if field is empty
                                                const tooltipMessage = validationResult.reason || (isEmpty ? "Please enter a Mapped value before enabling" : "");

                                                return isEditMode && tooltipMessage ?
                                                    (<TooltipOverlay message={tooltipMessage}>{toggleElement}</TooltipOverlay>)
                                                    : (toggleElement);
                                            })()}
                                        </td>
                                        <td className={`${rowPadding} ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                                            <ReviewDropdown
                                                value={Boolean(item[fields.reviewed])}
                                                onChange={(value) => onUpdate(String(item[fields.incoming]), { [fields.reviewed]: value } as Partial<T>)}
                                                disabled={!isEditMode || item[fields.reviewed]}
                                                itemId={String(item[fields.incoming])}
                                            />
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {/* Loading indicator lives INSIDE the scroll container so it's
                    visible at the bottom of the scrolled list (the container is
                    height-capped, so anything below it would be off-screen). */}
                {paginationLoading && (
                    <div className="tw-flex tw-justify-center tw-items-center tw-p-4 tw-gap-2">
                        <Spinner size="sm" label="Loading more records" />
                        <span className="tw-text-sm tw-text-ink-tertiary">Loading more records...</span>
                    </div>
                )}

                {/* Sentinel must live INSIDE the scroll container so the
                    IntersectionObserver fires as the user scrolls the table
                    body (not the page). */}
                <div ref={sentinelRef} style={{ height: '20px', width: '100%' }} data-testid="infinite-scroll-sentinel" />
            </div>
        </div>
    );
}

export default BaseMappingTable;
